// core/modals/inventory/addtax.jsx
import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import PropTypes from 'prop-types';
import Swal from 'sweetalert2';
import { taxActions } from '../../action/taxaction';

const AddTax = ({ onClose, onSuccess }) => {
    const dispatch = useDispatch();
    const [formData, setFormData] = useState({
        tax_name: '',
        tax_code: '',
        tax_rate: ''
    });
    const [loading, setLoading] = useState(false);

    const handleInputChange = (e) => {
        const { name, value } = e.target;

        // Special handling for tax_rate
        if (name === 'tax_rate') {
            if (value === '' || value === '-' || value === '.') {
                setFormData(prev => ({ ...prev, [name]: value }));
            } else if (!isNaN(value) && value >= 0 && value <= 100) {
                setFormData(prev => ({ ...prev, [name]: value }));
            }
        } else {
            setFormData(prev => ({
                ...prev,
                [name]: value
            }));
        }
    };

    const validateForm = () => {
        if (!formData.tax_name.trim()) {
            Swal.fire('Error', 'Tax name is required', 'error');
            return false;
        }
        if (!formData.tax_code.trim()) {
            Swal.fire('Error', 'Tax code is required', 'error');
            return false;
        }
        if (formData.tax_rate === '' || formData.tax_rate === null) {
            Swal.fire('Error', 'Tax rate is required', 'error');
            return false;
        }
        const rate = parseFloat(formData.tax_rate);
        if (isNaN(rate) || rate < 0 || rate > 100) {
            Swal.fire('Error', 'Please enter a valid tax rate between 0 and 100', 'error');
            return false;
        }
        return true;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!validateForm()) return;

        setLoading(true);
        try {
            const payload = {
                tax_name: formData.tax_name.trim(),
                tax_code: formData.tax_code.trim().toUpperCase(),
                tax_rate: parseFloat(formData.tax_rate)
            };

            const result = await dispatch(taxActions.createTax(payload));

            if (result && result.success) {
                Swal.fire({
                    icon: 'success',
                    title: 'Success!',
                    text: 'Tax rate created successfully',
                    timer: 2000,
                    showConfirmButton: false
                });

                if (onSuccess) onSuccess();
                if (onClose) onClose();
            } else {
                throw new Error(result?.message || 'Failed to create tax rate');
            }
        } catch (error) {
            Swal.fire({
                icon: 'error',
                title: 'Error!',
                text: error.message || 'Failed to create tax rate'
            });
        } finally {
            setLoading(false);
        }
    };

    if (!onClose) return null;

    return (
        <div className="modal fade show" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }}>
            <div className="modal-dialog modal-dialog-centered modal-lg">
                <div className="modal-content">
                    <div className="modal-header">
                        <h5 className="modal-title">Add Tax Rate</h5>
                        <button
                            type="button"
                            className="btn-close"
                            onClick={onClose}
                            disabled={loading}
                        ></button>
                    </div>

                    <div className="modal-body">
                        <form onSubmit={handleSubmit}>
                            <div className="row">
                                {/* Tax Name */}
                                <div className="col-md-6 mb-3">
                                    <label className="form-label">
                                        Tax Name <span className="text-danger">*</span>
                                    </label>
                                    <input
                                        type="text"
                                        name="tax_name"
                                        className="form-control"
                                        value={formData.tax_name}
                                        onChange={handleInputChange}
                                        placeholder="Enter tax name (e.g., GST)"
                                        disabled={loading}
                                        required
                                    />
                                </div>

                                {/* Tax Code */}
                                <div className="col-md-6 mb-3">
                                    <label className="form-label">
                                        Tax Code <span className="text-danger">*</span>
                                    </label>
                                    <input
                                        type="text"
                                        name="tax_code"
                                        className="form-control"
                                        value={formData.tax_code}
                                        onChange={handleInputChange}
                                        placeholder="Enter tax code (e.g., CGST)"
                                        disabled={loading}
                                        required
                                    />
                                    <small className="text-muted">Example: CGST, SGST, IGST</small>
                                </div>

                                {/* Tax Rate */}
                                <div className="col-md-6 mb-3">
                                    <label className="form-label">
                                        Tax Rate (%) <span className="text-danger">*</span>
                                    </label>
                                    <input
                                        type="number"
                                        name="tax_rate"
                                        className="form-control"
                                        value={formData.tax_rate}
                                        onChange={handleInputChange}
                                        placeholder="Enter tax rate (0-100)"
                                        min="0"
                                        max="100"

                                        disabled={loading}
                                        required
                                    />
                                    <small className="text-muted">Enter a number between 0 and 100</small>
                                </div>
                            </div>
                        </form>
                    </div>

                    <div className="modal-footer">
                        <button
                            type="button"
                            className="btn btn-secondary"
                            onClick={onClose}
                            disabled={loading}
                        >
                            Cancel
                        </button>
                        <button
                            type="button"
                            className="btn btn-primary"
                            onClick={handleSubmit}
                            disabled={loading}
                        >
                            {loading ? (
                                <>
                                    <span className="spinner-border spinner-border-sm me-2"></span>
                                    Creating...
                                </>
                            ) : 'Create Tax Rate'}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

AddTax.propTypes = {
    onClose: PropTypes.func.isRequired,
    onSuccess: PropTypes.func
};

export default AddTax;